import React, { useState, useEffect } from 'react';
import {
  History,
  Search,
  Filter,
  Calendar,
  Users,
  Mail,
  MessageSquare,
  CheckCircle,
  XCircle,
  Clock,
  Eye,
  RotateCcw,
  Download,
  BarChart3
} from 'lucide-react';

interface NotificationRecord {
  id: string;
  subject: string;
  message: string;
  recipients: string[];
  channels: string[];
  priority: 'low' | 'medium' | 'high' | 'urgent';
  status: 'sent' | 'delivered' | 'failed' | 'scheduled';
  sentAt: string;
  sentBy: string;
  templateUsed?: string;
  deliveryStats: {
    total: number;
    delivered: number;
    failed: number;
    opened: number;
  };
}

const NotificationHistory: React.FC = () => {
  const [notifications, setNotifications] = useState<NotificationRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterChannel, setFilterChannel] = useState('all');
  const [dateRange, setDateRange] = useState({ start: '', end: '' });
  const [selectedNotification, setSelectedNotification] = useState<NotificationRecord | null>(null);
  const [showStats, setShowStats] = useState(false);

  useEffect(() => {
    loadNotificationHistory();
  }, []);

  const loadNotificationHistory = async () => {
    try {
      setLoading(true);
      // Mock data - would be loaded from API
      const mockNotifications: NotificationRecord[] = [
        {
          id: '1',
          subject: 'Upcoming Examination Schedule',
          message: 'Dear Parents and Students, We would like to inform you about the upcoming examination schedule...',
          recipients: ['parent1@email.com', 'parent2@email.com', 'parent3@email.com'],
          channels: ['email', 'sms'],
          priority: 'high',
          status: 'delivered',
          sentAt: '2024-01-20T10:30:00Z',
          sentBy: 'admin@school.com',
          templateUsed: 'Exam Schedule Notification',
          deliveryStats: {
            total: 150,
            delivered: 147,
            failed: 3,
            opened: 98
          }
        },
        {
          id: '2',
          subject: 'Fee Payment Reminder - January',
          message: 'Dear Parents, This is a gentle reminder that the school fee payment for January is due...',
          recipients: ['parent1@email.com', 'parent4@email.com'],
          channels: ['email'],
          priority: 'medium',
          status: 'delivered',
          sentAt: '2024-01-18T09:15:00Z',
          sentBy: 'admin@school.com',
          templateUsed: 'Fee Payment Reminder',
          deliveryStats: {
            total: 85,
            delivered: 82,
            failed: 3,
            opened: 65
          }
        },
        {
          id: '3',
          subject: 'School Holiday Notification',
          message: 'Dear Parents and Students, We would like to inform you that the school will remain closed...',
          recipients: ['parent2@email.com', 'parent3@email.com'],
          channels: ['email', 'sms'],
          priority: 'low',
          status: 'scheduled',
          sentAt: '2024-01-25T08:00:00Z',
          sentBy: 'admin@school.com',
          templateUsed: 'Holiday Announcement',
          deliveryStats: {
            total: 120,
            delivered: 0,
            failed: 0,
            opened: 0
          }
        },
        {
          id: '4',
          subject: 'Parent-Teacher Meeting Invitation',
          message: 'Dear Parents, You are cordially invited to attend the Parent-Teacher Meeting...',
          recipients: ['parent1@email.com'],
          channels: ['email'],
          priority: 'medium',
          status: 'failed',
          sentAt: '2024-01-15T14:20:00Z',
          sentBy: 'admin@school.com',
          templateUsed: 'Parent-Teacher Meeting',
          deliveryStats: {
            total: 95,
            delivered: 87,
            failed: 8,
            opened: 45
          }
        }
      ];
      setNotifications(mockNotifications);
    } catch (error) {
      console.error('Error loading notification history:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleResendNotification = async (id: string) => {
    if (!window.confirm('Are you sure you want to resend this notification?')) return;

    try {
      // Resend notification logic
      console.log('Resending notification:', id);
      alert('Notification queued for resending!');
    } catch (error) {
      console.error('Error resending notification:', error);
    }
  };

  const exportHistory = () => {
    // Export functionality
    console.log('Exporting notification history...');
    alert('Export functionality would be implemented here');
  };

  const filteredNotifications = notifications.filter(notification => {
    const matchesSearch = notification.subject.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         notification.message.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = filterStatus === 'all' || notification.status === filterStatus;
    const matchesChannel = filterChannel === 'all' || notification.channels.includes(filterChannel);

    let matchesDate = true;
    if (dateRange.start && dateRange.end) {
      const notificationDate = new Date(notification.sentAt);
      const startDate = new Date(dateRange.start);
      const endDate = new Date(dateRange.end);
      matchesDate = notificationDate >= startDate && notificationDate <= endDate;
    }

    return matchesSearch && matchesStatus && matchesChannel && matchesDate;
  });

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'delivered':
        return <CheckCircle className="w-4 h-4 text-[#4ade80]" />;
      case 'failed':
        return <XCircle className="w-4 h-4 text-[#f38b83]" />;
      case 'scheduled':
        return <Clock className="w-4 h-4 text-[#f0b64c]" />;
      default:
        return <Clock className="w-4 h-4 text-[#8a7f6e]" />;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'delivered':
        return 'bg-[#173322] text-[#6ee7a0]';
      case 'failed':
        return 'bg-[#341a17] text-[#f7aca6]';
      case 'scheduled':
        return 'bg-[#332711] text-[#f5c876]';
      default:
        return 'bg-[#b9ad9b] text-[#5c5346]';
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'urgent':
        return 'bg-[#341a17] text-[#f7aca6]';
      case 'high':
        return 'bg-[#332711] text-[#f5c876]';
      case 'medium':
        return 'bg-[#332217] text-[#f0a67d]';
      case 'low':
        return 'bg-[#173322] text-[#6ee7a0]';
      default:
        return 'bg-[#b9ad9b] text-[#5c5346]';
    }
  };

  const totalStats = notifications.reduce(
    (acc, notification) => ({
      total: acc.total + notification.deliveryStats.total,
      delivered: acc.delivered + notification.deliveryStats.delivered,
      failed: acc.failed + notification.deliveryStats.failed,
      opened: acc.opened + notification.deliveryStats.opened
    }),
    { total: 0, delivered: 0, failed: 0, opened: 0 }
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-2 border-[#e2916d] border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-[#211c15] border border-[#3a3122] rounded-lg p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-[#8a7f6e] text-sm">Total Sent</p>
              <p className="text-2xl font-bold text-[#f3ede2]">{totalStats.total}</p>
            </div>
            <Mail className="w-8 h-8 text-[#e2916d]" />
          </div>
        </div>
        <div className="bg-[#211c15] border border-[#3a3122] rounded-lg p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-[#8a7f6e] text-sm">Delivered</p>
              <p className="text-2xl font-bold text-[#4ade80]">{totalStats.delivered}</p>
            </div>
            <CheckCircle className="w-8 h-8 text-[#4ade80]" />
          </div>
        </div>
        <div className="bg-[#211c15] border border-[#3a3122] rounded-lg p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-[#8a7f6e] text-sm">Failed</p>
              <p className="text-2xl font-bold text-[#f38b83]">{totalStats.failed}</p>
            </div>
            <XCircle className="w-8 h-8 text-[#f38b83]" />
          </div>
        </div>
        <div className="bg-[#211c15] border border-[#3a3122] rounded-lg p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-[#8a7f6e] text-sm">Opened</p>
              <p className="text-2xl font-bold text-[#e2916d]">{totalStats.opened}</p>
            </div>
            <Eye className="w-8 h-8 text-[#e2916d]" />
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-[#211c15] border border-[#3a3122] rounded-lg p-6">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
          <div className="flex flex-col sm:flex-row gap-4">
            {/* Search */}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-[#8a7f6e]" />
              <input
                type="text"
                placeholder="Search notifications..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 pr-4 py-2 border border-[#3a3122] rounded-lg focus:ring-2 focus:ring-[#e2916d] focus:border-transparent bg-[#17140f] text-[#f3ede2]"
              />
            </div>

            {/* Status Filter */}
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="px-3 py-2 border border-[#3a3122] rounded-lg focus:ring-2 focus:ring-[#e2916d] focus:border-transparent bg-[#17140f] text-[#f3ede2]"
            >
              <option value="all">All Status</option>
              <option value="delivered">Delivered</option>
              <option value="failed">Failed</option>
              <option value="scheduled">Scheduled</option>
            </select>

            {/* Channel Filter */}
            <select
              value={filterChannel}
              onChange={(e) => setFilterChannel(e.target.value)}
              className="px-3 py-2 border border-[#3a3122] rounded-lg focus:ring-2 focus:ring-[#e2916d] focus:border-transparent bg-[#17140f] text-[#f3ede2]"
            >
              <option value="all">All Channels</option>
              <option value="email">Email</option>
              <option value="sms">SMS</option>
            </select>
          </div>

          <div className="flex items-center space-x-2">
            <button
              onClick={() => setShowStats(!showStats)}
              className="px-4 py-2 border border-[#3a3122] text-[#b9ad9b] rounded-lg hover:bg-[#211c15] transition-colors flex items-center space-x-2"
            >
              <BarChart3 className="w-4 h-4" />
              <span>Stats</span>
            </button>
            <button
              onClick={exportHistory}
              className="px-4 py-2 border border-[#3a3122] text-[#b9ad9b] rounded-lg hover:bg-[#211c15] transition-colors flex items-center space-x-2"
            >
              <Download className="w-4 h-4" />
              <span>Export</span>
            </button>
          </div>
        </div>

        {/* Date Range */}
        <div className="flex flex-col sm:flex-row gap-4 mt-4">
          <div>
            <label className="block text-sm font-medium text-[#b9ad9b] mb-1">From Date</label>
            <input
              type="date"
              value={dateRange.start}
              onChange={(e) => setDateRange(prev => ({ ...prev, start: e.target.value }))}
              className="px-3 py-2 border border-[#3a3122] rounded-lg focus:ring-2 focus:ring-[#e2916d] focus:border-transparent bg-[#17140f] text-[#f3ede2]"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-[#b9ad9b] mb-1">To Date</label>
            <input
              type="date"
              value={dateRange.end}
              onChange={(e) => setDateRange(prev => ({ ...prev, end: e.target.value }))}
              className="px-3 py-2 border border-[#3a3122] rounded-lg focus:ring-2 focus:ring-[#e2916d] focus:border-transparent bg-[#17140f] text-[#f3ede2]"
            />
          </div>
          {(dateRange.start || dateRange.end) && (
            <div className="flex items-end">
              <button
                onClick={() => setDateRange({ start: '', end: '' })}
                className="px-3 py-2 text-sm text-[#e2916d] hover:text-[#f0a67d] transition-colors"
              >
                Clear
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Notifications List */}
      <div className="bg-[#211c15] border border-[#3a3122] rounded-lg">
        <div className="p-6 border-b border-[#3a3122]">
          <h3 className="text-lg font-semibold text-[#f3ede2] flex items-center">
            <History className="w-5 h-5 mr-2 text-[#e2916d]" />
            Notification History ({filteredNotifications.length})
          </h3>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-[#28221a]">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-[#8a7f6e] uppercase tracking-wider">
                  Subject & Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-[#8a7f6e] uppercase tracking-wider">
                  Recipients
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-[#8a7f6e] uppercase tracking-wider">
                  Delivery Stats
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-[#8a7f6e] uppercase tracking-wider">
                  Sent Date
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-[#8a7f6e] uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#3a3122]">
              {filteredNotifications.map((notification) => (
                <tr key={notification.id} className="hover:bg-[#28221a]">
                  <td className="px-6 py-4">
                    <div>
                      <div className="font-medium text-[#f3ede2] mb-1">{notification.subject}</div>
                      <div className="flex items-center space-x-2">
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(notification.status)}`}>
                          {notification.status.charAt(0).toUpperCase() + notification.status.slice(1)}
                        </span>
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${getPriorityColor(notification.priority)}`}>
                          {notification.priority.charAt(0).toUpperCase() + notification.priority.slice(1)}
                        </span>
                      </div>
                      <div className="flex items-center space-x-2 mt-1">
                        {notification.channels.map((channel) => (
                          <span key={channel} className="flex items-center text-xs text-[#8a7f6e]">
                            {channel === 'email' ? <Mail className="w-3 h-3 mr-1" /> : <MessageSquare className="w-3 h-3 mr-1" />}
                            {channel}
                          </span>
                        ))}
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center">
                      <Users className="w-4 h-4 text-[#8a7f6e] mr-2" />
                      <span className="text-sm text-[#b9ad9b]">{notification.deliveryStats.total} recipients</span>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="text-sm">
                      <div className="flex items-center space-x-4">
                        <div className="flex items-center">
                          <CheckCircle className="w-3 h-3 text-[#4ade80] mr-1" />
                          <span className="text-[#4ade80]">{notification.deliveryStats.delivered}</span>
                        </div>
                        <div className="flex items-center">
                          <XCircle className="w-3 h-3 text-[#f38b83] mr-1" />
                          <span className="text-[#f38b83]">{notification.deliveryStats.failed}</span>
                        </div>
                        <div className="flex items-center">
                          <Eye className="w-3 h-3 text-[#e2916d] mr-1" />
                          <span className="text-[#e2916d]">{notification.deliveryStats.opened}</span>
                        </div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="text-sm text-[#b9ad9b]">
                      {new Date(notification.sentAt).toLocaleDateString()}
                    </div>
                    <div className="text-xs text-[#8a7f6e]">
                      {new Date(notification.sentAt).toLocaleTimeString()}
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center space-x-2">
                      <button
                        onClick={() => setSelectedNotification(notification)}
                        className="text-[#e2916d] hover:text-[#f0a67d] transition-colors"
                        title="View Details"
                      >
                        <Eye className="w-4 h-4" />
                      </button>
                      {notification.status === 'failed' && (
                        <button
                          onClick={() => handleResendNotification(notification.id)}
                          className="text-[#f0b64c] hover:text-[#f5c876] transition-colors"
                          title="Resend"
                        >
                          <RotateCcw className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {filteredNotifications.length === 0 && (
          <div className="text-center py-12">
            <History className="w-12 h-12 text-[#8a7f6e] mx-auto mb-4" />
            <h3 className="text-lg font-medium text-[#f3ede2] mb-2">No notifications found</h3>
            <p className="text-[#8a7f6e]">
              {searchTerm || filterStatus !== 'all' || filterChannel !== 'all'
                ? 'No notifications match your search criteria.'
                : 'No notifications have been sent yet.'
              }
            </p>
          </div>
        )}
      </div>

      {/* Notification Detail Modal */}
      {selectedNotification && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-[#211c15] rounded-lg w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-6 border-b border-[#3a3122]">
              <h2 className="text-xl font-semibold text-[#f3ede2]">Notification Details</h2>
              <button
                onClick={() => setSelectedNotification(null)}
                className="text-[#8a7f6e] hover:text-[#b9ad9b]"
              >
                <XCircle className="w-6 h-6" />
              </button>
            </div>

            <div className="p-6 space-y-6">
              <div>
                <h3 className="text-lg font-medium text-[#f3ede2] mb-2">{selectedNotification.subject}</h3>
                <div className="flex items-center space-x-2 mb-4">
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(selectedNotification.status)}`}>
                    {selectedNotification.status.charAt(0).toUpperCase() + selectedNotification.status.slice(1)}
                  </span>
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${getPriorityColor(selectedNotification.priority)}`}>
                    {selectedNotification.priority.charAt(0).toUpperCase() + selectedNotification.priority.slice(1)}
                  </span>
                </div>
              </div>

              <div>
                <h4 className="font-medium text-[#b9ad9b] mb-2">Message</h4>
                <div className="bg-[#28221a] border border-[#3a3122] rounded-lg p-4">
                  <p className="text-sm text-[#b9ad9b] whitespace-pre-wrap">{selectedNotification.message}</p>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <h4 className="font-medium text-[#b9ad9b] mb-2">Sent Information</h4>
                  <div className="space-y-2 text-sm">
                    <div>
                      <span className="text-[#8a7f6e]">Sent by:</span>
                      <span className="text-[#b9ad9b] ml-2">{selectedNotification.sentBy}</span>
                    </div>
                    <div>
                      <span className="text-[#8a7f6e]">Sent at:</span>
                      <span className="text-[#b9ad9b] ml-2">{new Date(selectedNotification.sentAt).toLocaleString()}</span>
                    </div>
                    {selectedNotification.templateUsed && (
                      <div>
                        <span className="text-[#8a7f6e]">Template:</span>
                        <span className="text-[#b9ad9b] ml-2">{selectedNotification.templateUsed}</span>
                      </div>
                    )}
                  </div>
                </div>

                <div>
                  <h4 className="font-medium text-[#b9ad9b] mb-2">Delivery Statistics</h4>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-[#8a7f6e]">Total Recipients:</span>
                      <span className="text-[#b9ad9b]">{selectedNotification.deliveryStats.total}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-[#4ade80]">Delivered:</span>
                      <span className="text-[#4ade80]">{selectedNotification.deliveryStats.delivered}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-[#f38b83]">Failed:</span>
                      <span className="text-[#f38b83]">{selectedNotification.deliveryStats.failed}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-[#e2916d]">Opened:</span>
                      <span className="text-[#e2916d]">{selectedNotification.deliveryStats.opened}</span>
                    </div>
                  </div>
                </div>
              </div>

              <div>
                <h4 className="font-medium text-[#b9ad9b] mb-2">Channels Used</h4>
                <div className="flex items-center space-x-2">
                  {selectedNotification.channels.map((channel) => (
                    <span key={channel} className="flex items-center bg-[#211c15] text-[#b9ad9b] px-3 py-1 rounded-full text-sm">
                      {channel === 'email' ? <Mail className="w-3 h-3 mr-1" /> : <MessageSquare className="w-3 h-3 mr-1" />}
                      {channel}
                    </span>
                  ))}
                </div>
              </div>
            </div>

            <div className="flex items-center justify-end space-x-3 p-6 border-t border-[#3a3122]">
              {selectedNotification.status === 'failed' && (
                <button
                  onClick={() => {
                    handleResendNotification(selectedNotification.id);
                    setSelectedNotification(null);
                  }}
                  className="px-4 py-2 bg-[#f0b64c] hover:bg-[#f0b64c] text-white rounded-lg transition-colors flex items-center space-x-2"
                >
                  <RotateCcw className="w-4 h-4" />
                  <span>Resend</span>
                </button>
              )}
              <button
                onClick={() => setSelectedNotification(null)}
                className="px-4 py-2 bg-[#e2916d] hover:bg-[#f0a67d] text-white rounded-lg transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default NotificationHistory;